import { motion } from "framer-motion";
import { Video, Camera, Users } from "lucide-react";

const videographyFeatures = [
  "Two videographers on every booking",
  "Full day coverage from bridal prep to first dance",
  "20 to 30 minute cinematic wedding film",
  "3 to 4 minute highlight film within 72 hours",
  "Full ceremony and speeches edits (multi camera)",
  "Cake cutting and first dance edits",
  "Drone footage (weather and location permitting)",
  "Private online delivery",
];

const photographyFeatures = [
  "Full day coverage from prep to first dance",
  "Natural, documentary style photography",
  "Key moments captured as they happen",
  "500+ professionally edited photos",
  "Online gallery for easy sharing and downloads",
  "Designed to match the style of your film",
];

const combinedBenefits = [
  "One seamless team that works together every weekend",
  "Consistent visual style across photos and film",
  "Better coordination means no missed moments",
  "One supplier, one point of contact, no stress",
  "More cost effective than booking separately",
];

function ServiceBlock({ icon: Icon, title, subtitle, features, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      data-testid={`service-block-${index}`}
      className="bg-[#11100F] border border-white/[0.06] rounded-lg p-7 md:p-9"
    >
      <Icon className="w-6 h-6 text-[#C2A878] mb-4" strokeWidth={1.5} />
      <h3 className="font-serif text-xl sm:text-2xl text-[#F5F5F0] tracking-tight mb-2">
        {title}
      </h3>
      {subtitle && (
        <p className="text-sm text-[#A19D94]/60 font-sans mb-5">{subtitle}</p>
      )}
      <ul className="space-y-2.5">
        {features.map((f, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 text-sm text-[#A19D94]/80 font-sans"
          >
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C2A878] flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function ServicesSection() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#0A0908] py-24 md:py-32"
      aria-label="Wedding videography and photography services in Essex"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Our Services
          </p>
          <h2
            data-testid="services-heading"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight font-light text-[#F5F5F0] mb-3"
          >
            Wedding Videography & Photography Essex
          </h2>
          <p className="text-sm text-[#A19D94] font-sans max-w-2xl mx-auto leading-relaxed">
            We offer wedding videography, photography, and combined packages across Essex, London, Kent, and the South East. Every service is designed around natural, story-led coverage with a discreet approach.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ServiceBlock
            icon={Video}
            title="Wedding Videography Essex"
            subtitle="Two videographers. Full day. Cinematic film."
            features={videographyFeatures}
            index={0}
          />
          <ServiceBlock
            icon={Camera}
            title="Wedding Photography Essex"
            subtitle="Documentary style. Natural moments. Editorial eye."
            features={photographyFeatures}
            index={1}
          />
          <ServiceBlock
            icon={Users}
            title="Combined Photo & Video"
            subtitle="One team. One style. Complete coverage."
            features={combinedBenefits}
            index={2}
          />
        </div>
      </div>
    </section>
  );
}
